import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Plus, Search, Phone, Mail, DollarSign,
  ChevronLeft, ChevronRight, Edit3, Trash2,
  X, FileText, TrendingUp, TrendingDown, Wallet, ShoppingBag,
} from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import Skeleton from '../components/common/Skeleton';
import StatCard from '../components/common/StatCard';
import {
  fetchSuppliers, createSupplier, updateSupplier, deleteSupplier,
  fetchSupplierTransactions, createSupplierPurchase, createSupplierPayment,
  clearSupplier, setPage,
} from '../redux/slices/supplierSlice';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const categories = ['All', 'Active', 'Inactive'];

export default function Suppliers() {
  const dispatch = useDispatch();
  const { suppliers, loading, totalPages, currentPage, transactions } = useSelector((state) => state.suppliers);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [purchaseDesc, setPurchaseDesc] = useState('');
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDesc, setPaymentDesc] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '', contactPerson: '', phoneNumber: '', email: '',
    address: '', cnicNumber: '', taxNumber: '', notes: '',
  });

  useEffect(() => {
    dispatch(fetchSuppliers({ page: 1, limit: 50 }));
  }, [dispatch]);

  const handlePageChange = (page) => {
    dispatch(setPage(page));
    dispatch(fetchSuppliers({ page, limit: 50 }));
  };

  const filteredSuppliers = useMemo(() => {
    let result = suppliers;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          (s.companyName || s.name)?.toLowerCase().includes(q) ||
          s.phoneNumber?.toLowerCase().includes(q) ||
          s.contactPerson?.toLowerCase().includes(q)
      );
    }
    if (statusFilter === 'Active') {
      result = result.filter((s) => s.status === 'ACTIVE');
    } else if (statusFilter === 'Inactive') {
      result = result.filter((s) => s.status === 'INACTIVE');
    }
    return result;
  }, [suppliers, searchQuery, statusFilter]);

  const resetForm = () => {
    setFormData({
      companyName: '', contactPerson: '', phoneNumber: '', email: '',
      address: '', cnicNumber: '', taxNumber: '', notes: '',
    });
  };

  const openCreateForm = () => {
    setEditingSupplier(null);
    resetForm();
    setShowForm(true);
  };

  const openEditForm = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      companyName: supplier.companyName || '',
      contactPerson: supplier.contactPerson || '',
      phoneNumber: supplier.phoneNumber || '',
      email: supplier.email || '',
      address: supplier.address || '',
      cnicNumber: supplier.cnicNumber || '',
      taxNumber: supplier.taxNumber || '',
      notes: supplier.notes || '',
    });
    setShowForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.companyName.trim()) { toast.error('Company name is required'); return; }
    if (!formData.phoneNumber.trim()) { toast.error('Phone number is required'); return; }

    try {
      if (editingSupplier) {
        await dispatch(updateSupplier({
          id: editingSupplier.supplierId || editingSupplier._id,
          supplierData: formData,
        })).unwrap();
        toast.success('Supplier updated');
      } else {
        await dispatch(createSupplier(formData)).unwrap();
        toast.success('Supplier created');
      }
      setShowForm(false);
      resetForm();
      setEditingSupplier(null);
    } catch (err) {
      toast.error(err?.message || 'Operation failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteSupplier(deleteTarget.supplierId || deleteTarget._id)).unwrap();
      setDeleteTarget(null);
      toast.success('Supplier deactivated');
    } catch (err) {
      toast.error(err?.message || 'Failed to delete supplier');
    } finally {
      setDeleting(false);
    }
  };

  const handleSelectSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    dispatch(fetchSupplierTransactions({ id: supplier.supplierId || supplier._id, params: { page: 1, limit: 50 } }));
  };

  const handleRecordPurchase = async () => {
    if (!selectedSupplier || !purchaseAmount) return;
    setPurchaseLoading(true);
    try {
      await dispatch(createSupplierPurchase({
        id: selectedSupplier.supplierId || selectedSupplier._id,
        purchaseData: { amount: parseFloat(purchaseAmount), description: purchaseDesc, paymentMethod: 'CREDIT' }
      })).unwrap();
      setShowPurchaseModal(false);
      setPurchaseAmount('');
      setPurchaseDesc('');
      toast.success('Purchase recorded');
    } catch (err) {
      toast.error(err?.message || 'Failed to record purchase');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!selectedSupplier || !paymentAmount) return;
    setPaymentLoading(true);
    try {
      await dispatch(createSupplierPayment({
        id: selectedSupplier.supplierId || selectedSupplier._id,
        paymentData: { amount: parseFloat(paymentAmount), description: paymentDesc, paymentMethod: 'CASH' }
      })).unwrap();
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentDesc('');
      toast.success('Payment recorded');
    } catch (err) {
      toast.error(err?.message || 'Failed to record payment');
    } finally {
      setPaymentLoading(false);
    }
  };

  const formatCurrency = (val) => val?.toLocaleString() || '0';

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Supplier Management"
        description="Manage suppliers, track purchases and payments"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" icon={Plus} onClick={openCreateForm}>
              Add Supplier
            </Button>
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by company, phone, or contact person..."
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl focus:border-primary-500 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 focus:outline-none"
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="card" />
              ))}
            </div>
          ) : filteredSuppliers.length === 0 ? (
            <EmptyState
              icon={Building2}
              title="No suppliers found"
              description={searchQuery ? 'Try adjusting your search' : 'Add your first supplier'}
              actionLabel="Add Supplier"
              onAction={openCreateForm}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <AnimatePresence mode="popLayout">
                {filteredSuppliers.map((supplier) => (
                  <motion.div
                    key={supplier._id || supplier.supplierId}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    onClick={() => handleSelectSupplier(supplier)}
                    className={`cursor-pointer bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow ${
                      selectedSupplier?._id === supplier._id || selectedSupplier?.id === supplier.id
                        ? 'ring-2 ring-primary-500'
                        : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
                          <Building2 size={20} className="text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {supplier.companyName || supplier.name}
                          </h3>
                          {supplier.contactPerson && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{supplier.contactPerson}</p>
                          )}
                        </div>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        supplier.status === 'ACTIVE'
                          ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        {supplier.status || 'ACTIVE'}
                      </span>
                    </div>

                    <div className="space-y-1.5 text-xs text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <Phone size={13} className="flex-shrink-0" />
                        <span>{supplier.phoneNumber}</span>
                      </div>
                      {supplier.taxNumber && (
                        <div className="flex items-center gap-2">
                          <FileText size={13} className="flex-shrink-0" />
                          <span>NTN: {supplier.taxNumber}</span>
                        </div>
                      )}
                      {supplier.totalPurchases > 0 && (
                        <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                          <ShoppingBag size={13} className="flex-shrink-0" />
                          <span>Purchases: {supplier.totalPurchases} (Rs.{formatCurrency(supplier.totalPurchasesAmount)})</span>
                        </div>
                      )}
                      {supplier.currentCredit > 0 && (
                        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                          <DollarSign size={13} className="flex-shrink-0" />
                          <span>Credit: Rs.{formatCurrency(supplier.currentCredit)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <Button variant="secondary" size="xs" icon={Edit3} onClick={(e) => { e.stopPropagation(); openEditForm(supplier); }}>
                        Edit
                      </Button>
                      <Button variant="danger" size="xs" icon={Trash2} onClick={(e) => { e.stopPropagation(); setDeleteTarget(supplier); }}>
                        Deactivate
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                    page === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {selectedSupplier ? (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Supplier Details</h3>
                <button
                  onClick={() => setSelectedSupplier(null)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold">
                    {(selectedSupplier.companyName || '?').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{selectedSupplier.companyName}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{selectedSupplier.phoneNumber}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                    <ShoppingBag size={16} className="mx-auto mb-1 text-blue-500" />
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total Purchases</p>
                    <p className="text-sm font-bold text-blue-700 dark:text-blue-400">
                      Rs.{formatCurrency(selectedSupplier.totalPurchasesAmount)}
                    </p>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
                    <DollarSign size={16} className="mx-auto mb-1 text-amber-500" />
                    <p className="text-xs text-gray-500 dark:text-gray-400">Current Credit</p>
                    <p className="text-sm font-bold text-amber-700 dark:text-amber-400">
                      Rs.{formatCurrency(selectedSupplier.currentCredit)}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="primary" size="sm" icon={ShoppingBag} fullWidth onClick={() => setShowPurchaseModal(true)}>
                    Purchase
                  </Button>
                  <Button variant="secondary" size="sm" icon={Wallet} fullWidth onClick={() => setShowPaymentModal(true)}>
                    Payment
                  </Button>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Transaction History</h4>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700/50 max-h-80 overflow-y-auto">
                  {transactions.length === 0 ? (
                    <div className="p-6 text-center text-gray-400 dark:text-gray-500">
                      <Wallet size={28} className="mx-auto mb-2" />
                      <p className="text-sm">No transactions yet</p>
                    </div>
                  ) : (
                    transactions.map((t, idx) => (
                      <div key={t._id || t.transactionId || idx} className="px-4 py-3 flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          t.type === 'PURCHASE'
                            ? 'bg-red-50 dark:bg-red-900/20 text-red-500'
                            : 'bg-green-50 dark:bg-green-900/20 text-green-500'
                        }`}>
                          {t.type === 'PURCHASE' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 dark:text-white">
                            {t.type === 'PURCHASE' ? 'Goods Purchased' : 'Payment Made'}
                          </p>
                          <p className="text-[10px] text-gray-400">{t.transactionDate}</p>
                          {t.description && (
                            <p className="text-[10px] text-gray-500 truncate">{t.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className={`text-xs font-semibold ${
                            t.type === 'PURCHASE' ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {t.type === 'PURCHASE' ? '+' : '-'} Rs.{formatCurrency(t.amount)}
                          </p>
                          <p className="text-[10px] text-gray-400">Bal: Rs.{formatCurrency(t.balanceAfter)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <EmptyState
                icon={Building2}
                title="Select a supplier"
                description="Click on a supplier to view their details and transactions"
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingSupplier(null); }}
        size="md"
        title={editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Company Name *</label>
              <input name="companyName" value={formData.companyName} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Phone Number *</label>
              <input name="phoneNumber" value={formData.phoneNumber} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Contact Person</label>
              <input name="contactPerson" value={formData.contactPerson} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
              <input name="email" type="email" value={formData.email} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tax Number (NTN)</label>
              <input name="taxNumber" value={formData.taxNumber} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Address</label>
              <input name="address" value={formData.address} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">CNIC</label>
              <input name="cnicNumber" value={formData.cnicNumber} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Notes</label>
              <textarea name="notes" rows={2} value={formData.notes} onChange={handleFormChange}
                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 resize-none" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" fullWidth onClick={() => { setShowForm(false); setEditingSupplier(null); }}>
              Cancel
            </Button>
            <Button variant="primary" fullWidth onClick={handleSubmit} loading={loading}>
              {editingSupplier ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showPurchaseModal}
        onClose={() => { setShowPurchaseModal(false); setPurchaseAmount(''); setPurchaseDesc(''); }}
        size="sm"
        title={`Record Purchase - ${selectedSupplier?.companyName || ''}`}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Purchase Amount</label>
            <div className="relative">
              <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="number" min="0" step="0.01" value={purchaseAmount}
                onChange={(e) => setPurchaseAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                autoFocus />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Description</label>
            <input type="text" value={purchaseDesc} onChange={(e) => setPurchaseDesc(e.target.value)}
              placeholder="What was purchased?"
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" fullWidth onClick={() => { setShowPurchaseModal(false); setPurchaseAmount(''); setPurchaseDesc(''); }}>
              Cancel
            </Button>
            <Button variant="primary" fullWidth onClick={handleRecordPurchase} loading={purchaseLoading} disabled={!purchaseAmount}>
              Record Purchase
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setPaymentAmount(''); setPaymentDesc(''); }}
        size="sm"
        title={`Record Payment - ${selectedSupplier?.companyName || ''}`}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Payment Amount</label>
            <div className="relative">
              <DollarSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="number" min="0" step="0.01" value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                autoFocus />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Description</label>
            <input type="text" value={paymentDesc} onChange={(e) => setPaymentDesc(e.target.value)}
              placeholder="Payment note"
              className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" fullWidth onClick={() => { setShowPaymentModal(false); setPaymentAmount(''); setPaymentDesc(''); }}>
              Cancel
            </Button>
            <Button variant="primary" fullWidth onClick={handleRecordPayment} loading={paymentLoading} disabled={!paymentAmount}>
              Record Payment
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Deactivate Supplier"
        message={`Are you sure you want to deactivate "${deleteTarget?.companyName || deleteTarget?.name}"?`}
        confirmLabel="Deactivate"
        loading={deleting}
        variant="danger"
      />
    </motion.div>
  );
}
